import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { CheckCircle2, Building2, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import Logo from "@/components/Logo";

export default function ClientRegister() {
  const { regToken } = useParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ full_name: "", job_title: "", email: "", phone: "", company_website: "" });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/public/join/${regToken}`).then((r) => setInfo(r.data)).catch(() => setNotFound(true));
  }, [regToken]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/public/join/${regToken}`, form);
      setDone(data);
      toast.success("Your card is ready!");
    } catch (err) { toast.error(err.response?.data?.detail || "Registration failed"); }
    finally { setSaving(false); }
  };

  if (notFound) return <div className="min-h-screen flex flex-col items-center justify-center gap-4"><Logo /><p className="text-[#8B9BB4]">Invalid or expired registration link.</p></div>;
  if (!info) return <div className="min-h-screen flex items-center justify-center text-[#8B9BB4]">Loading…</div>;

  const gold = info.brand_color || "#D4AF37";
  const cardUrl = done ? `${window.location.origin}/c/${done.slug}` : "";

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 iw-noise relative">
      <div className="w-full max-w-md relative z-10">
        <div className="iw-card p-8 fade-up">
          <div className="flex items-center gap-3 mb-6">
            {info.logo_url ? <img src={info.logo_url.startsWith("http") ? info.logo_url : `${process.env.REACT_APP_BACKEND_URL}${info.logo_url}`} alt="" className="h-12 w-12 object-contain rounded-lg bg-white/90 p-1.5" />
              : <div className="h-12 w-12 rounded-lg flex items-center justify-center" style={{ background: gold }}><Building2 className="text-[#081426]" /></div>}
            <div>
              <p className="overline" style={{ color: gold }}>Team Registration</p>
              <h1 className="font-display text-2xl font-bold">{info.company_name}</h1>
            </div>
          </div>

          {done ? (
            <div className="text-center py-4">
              <CheckCircle2 className="mx-auto mb-3" size={44} style={{ color: gold }} />
              <h2 className="font-display text-xl font-bold">You're all set!</h2>
              <p className="text-sm text-[#8B9BB4] mt-1 mb-5">Your digital business card under {done.company_name} is live.</p>
              <div className="bg-white p-3 rounded-xl inline-block mb-4"><QRCodeCanvas value={cardUrl} size={150} level="H" fgColor="#081426" /></div>
              <a href={cardUrl} target="_blank" rel="noreferrer" className="btn-gold w-full py-3 rounded-full flex items-center justify-center gap-2" style={{ background: gold }} data-testid="view-my-card-btn"><ExternalLink size={16} /> View My Card</a>
            </div>
          ) : info.full ? (
            <p className="text-center text-red-400 py-6">This company account is full. Please contact your administrator.</p>
          ) : (
            <>
              <p className="text-sm text-[#8B9BB4] mb-4">Create your branded digital business card in seconds. <span className="text-[#F6F1E4]">{info.seats_used}/{info.seat_limit}</span> seats used.</p>
              <form onSubmit={submit} className="space-y-3">
                <input type="text" name="company_website" tabIndex={-1} autoComplete="off" value={form.company_website} onChange={(e) => setForm({ ...form, company_website: e.target.value })} style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }} aria-hidden="true" />
                <input className="iw-input" placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="join-name" />
                <input className="iw-input" placeholder="Job title" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} data-testid="join-title" />
                <input className="iw-input" type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="join-email" />
                <input className="iw-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="join-phone" />
                <button disabled={saving} className="btn-gold w-full py-3 rounded-full" style={{ background: gold }} data-testid="join-submit">{saving ? "Creating…" : "Create My Card"}</button>
              </form>
            </>
          )}
        </div>
        <div className="flex justify-center mt-5 opacity-60"><Logo size={20} /></div>
      </div>
    </div>
  );
}
